/**
 * Constants for the Browser Extension feature.
 * Centralizes magic values, configuration, and shared constants.
 */

// =============================================================================
// DATE & TIME CONSTANTS
// =============================================================================

/** Default number of days for date range filters */
export const DEFAULT_DATE_RANGE_DAYS = 30

/** Number of past days to show in heatmaps */
export const HEATMAP_PAST_DAYS = 90

// =============================================================================
// API & DATA CONSTANTS
// =============================================================================

/** Debounce delay for API calls on filter changes (in milliseconds) */
export const FILTER_DEBOUNCE_MS = 300

// =============================================================================
// CHART COLORS
// =============================================================================

/** Color palette for charts and visualizations */
export const CHART_COLORS = {
  /** Primary blue - main chart color */
  primary: '#3b82f6',
  /** Light blue - secondary elements */
  primaryLight: '#60a5fa',
  /** Dark blue - emphasized elements */
  primaryDark: '#2563eb',
  /** Gray for empty/zero values */
  empty: '#f3f4f6',
} as const

/** Blue theme gradient for heatmaps (from low to high intensity) */
export const HEATMAP_BLUE_THEME = [
  '#f3f4f6', // gray-100 (0 sessions)
  '#bfdbfe', // blue-200
  '#60a5fa', // blue-400
  '#2563eb', // blue-600
  '#1e40af', // blue-800
] as const

/** Blue theme gradient for heatmaps in dark mode (from low to high intensity) */
export const HEATMAP_BLUE_THEME_DARK = [
  '#1f2937', // gray-800 (0 sessions)
  '#1e3a8a', // blue-900
  '#1d4ed8', // blue-700
  '#3b82f6', // blue-500
  '#93c5fd', // blue-300
] as const

// =============================================================================
// STATUS STYLES
// =============================================================================

/** Status badge style configurations */
export const STATUS_STYLES = {
  active: {
    badge:
      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    dot: 'bg-green-500',
  },
  inactive: {
    badge: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400',
    dot: 'bg-gray-500',
  },
  warning: {
    badge:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    dot: 'bg-yellow-500',
  },
  error: {
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    dot: 'bg-red-500',
  },
} as const

export type StatusType = keyof typeof STATUS_STYLES
